import {action, makeObservable, observable, runInAction} from "mobx";

import {AdminApi} from "../../../api/api";
import {DocumentGroupListInput} from "../../../api/values/document-group-list-input";
import {DocumentGroupOutput} from "../../../api/values/document-group-output";
import {Stores} from "../../../stores";
import {Permission} from "../../../stores/authentication-store";
import {PageStoreConstructorOptions} from "../../../utils/page-store";
import {PageRouter} from "../../../utils/route-util";
import {validateLength} from "../../../utils/validation/functions/length";
import {validateSlug} from "../../../utils/validation/functions/slug";

/* eslint-disable max-classes-per-file */
class DocumentGroupItem {
    public key: string = "";

    public title: string = "";

    public constructor(args?: Partial<DocumentGroupItem>) {
        Object.assign(this, args);
    }
}

export class DocumentGroupListPageStore {
    @observable
    public items: DocumentGroupItem[] = [];
    private adminApi: AdminApi;
    private stores: Stores;
    private pageRouter: PageRouter;

    @observable
    public mediaId: string = "";

    constructor(options: PageStoreConstructorOptions) {
        makeObservable(this);

        this.adminApi = options.adminApi;
        this.stores = options.stores;
        this.pageRouter = options.pageRouter;
    }

    public async initialize({query}: any = {}) {
        this.stores.authenticationStore.checkAnyPermissionExists(Permission.EDIT_GENERAL_RESOURCES);

        this.setMediaId(query.mediaId || "");

        await this.load();
    }

    @action.bound
    public setMediaId(s: string) {
        this.mediaId = s;
    }

    @action.bound
    public async load() {
        const result = await this.adminApi.getDocumentGroups(
            new DocumentGroupListInput({
                mediaId: this.mediaId,
            }),
        );

        runInAction(() => {
            this.items = result.documentGroups.map(
                (item) =>
                    new DocumentGroupItem({
                        key: item.key,
                        title: item.title,
                    }),
            );
        });
    }

    @action.bound
    /**
     * ドキュメントグループ追加時の識別子（key/slug相当）をバリデーションする。
     * UI入力中のエラー表示と、作成前の最終チェックの両方から利用する。
     *
     * @param key 検証対象の識別子（ドキュメントグループ key）
     * @returns エラーがある場合は表示用メッセージ、問題ない場合は `undefined`
     */
    public async validateDocumentGroupKey(key: string): Promise<string | undefined> {
        if (!key) {
            // 未入力時は PopoverCreateButton 側の「追加不可」制御に任せる。
            return undefined;
        }

        // MEMO:
        // 100文字超過で設定しているが、他Slugは50文字なので見直してもいいかもしれない。
        // Server側も修正が必要なので、要件に応じて適宜調整する。

        // ValidationFunction は同期/非同期どちらも返しうるため Promise.resolve で吸収する。
        const lengthErrors = await Promise.resolve(validateLength(1, 100)(key));
        const slugErrors = await Promise.resolve(validateSlug()(key));
        const validationErrors = [...lengthErrors, ...slugErrors];
        if (validationErrors.length > 0) {
            // 複数エラーがあっても先頭のみ表示し、メッセージ量を抑える。
            return validationErrors[0].errorMessage;
        }

        // 既存一覧との重複は API 呼び出し前に明示的に弾く。
        if (this.items.map((item) => item.key).includes(key)) {
            return `"${key}" は既に使われています`;
        }

        return undefined;
    }

    @action.bound
    /**
     * ドキュメントグループを新規作成し、作成後に詳細画面へ遷移する。
     * 作成直前に識別子のバリデーションを再実行し、不正な場合は API を呼ばない。
     *
     * @param key 作成するドキュメントグループの識別子（ドキュメントグループ key）
     * @returns 非同期処理完了を示す Promise（明示的な返却値はなし）
     */
    public async createDocumentGroup(key: string) {
        // UI 側でも入力中バリデーションを行うが、作成直前に再度検証して防御する。
        const validationError = await this.validateDocumentGroupKey(key);
        if (validationError) {
            return;
        }

        await this.adminApi.putDocumentGroup(
            {mediaId: this.mediaId},
            {key},
            new DocumentGroupOutput({
                key,
                slugs: [],
                title: key,
            }),
        );

        this.pageRouter.pushRoute("/admin/[mediaId]/document-groups/[key]", {
            mediaId: this.mediaId,
            key,
        });
    }
}
